import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dumbbell, CheckCircle2, XCircle, Clock, AlertCircle,
  Loader2, CalendarDays, TrendingUp, User,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABEL = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const CAL_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const STATUS_CLS = {
  completed: { text: "text-green-500", bg: "bg-green-500", light: "bg-green-500/10", icon: CheckCircle2, label: "Done" },
  missed: { text: "text-red-500", bg: "bg-red-500", light: "bg-red-500/10", icon: XCircle, label: "Missed" },
  upcoming: { text: "text-amber-400", bg: "bg-amber-400", light: "bg-amber-400/10", icon: Clock, label: "Upcoming" },
};

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toYMD(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className={`text-2xl font-black font-heading ${color}`}>{value}</p>
          </div>
          <div className={`w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── DayDetail bottom-sheet ────────────────────────────────────────────────────
function DayDetail({ session, allowMemberToComplete, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  if (!session) return null;
  const st = STATUS_CLS[session.status] || STATUS_CLS.upcoming;
  const Icon = st.icon;
  const dateLabel = session.date
    ? parseLocalDate(session.date)?.toLocaleDateString("en-IN", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    })
    : "";

  const handleMarkComplete = async () => {
    if (!window.confirm("Are you sure you want to mark this workout as completed?")) return;
    const token = localStorage.getItem("member_token");
    if (!token) return;
    setLoading(true);
    try {
      await api.put(`/member-portal/workouts/schedule/${session.id}/status`,
        { status: "completed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Workout marked as completed");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkUncomplete = async () => {
    if (!window.confirm("Are you sure you want to mark this workout as uncompleted?")) return;
    const token = localStorage.getItem("member_token");
    if (!token) return;
    setLoading(true);
    try {
      await api.put(`/member-portal/workouts/schedule/${session.id}/status`,
        { status: "upcoming" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Workout marked as uncompleted");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-card border border-border rounded-2xl p-5 space-y-4 shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-sm leading-snug">{dateLabel}</p>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">{session.dayOfWeek}</p>
            </div>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${st.light} ${st.text}`}>
              <Icon className="h-3 w-3" />{st.label}
            </span>
          </div>
          {session.status === "completed" && session.completedAt && (() => {
            const date = new Date(session.completedAt);
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const ampm = hours >= 12 ? "pm" : "am";
            hours = hours % 12;
            hours = hours ? hours : 12;
            const displayDateTime = `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
            const isMember = session.completedBy?.startsWith("member:");

            return (
              <div className="text-[10px] flex-col tracking-tight  flex items-center text-muted-foreground font-mono mt-1.5 space-y-0.5">
                <p className="font-semibold text-foreground">
                  Done by {isMember ? "Member" : "Staff"}: {session.completedByName || (isMember ? "Member" : "Staff")}
                </p>
                <p className="text-muted-foreground font-medium">{displayDateTime}</p>
              </div>
            );
          })()}
        </div>

        {session.status === "upcoming" && allowMemberToComplete && (
          <button
            onClick={handleMarkComplete}
            disabled={loading}
            className="w-full py-2 flex items-center justify-center gap-2 rounded-xl bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
          >
            {loading ? "Updating..." : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Mark as Completed
              </>
            )}
          </button>
        )}

        {session.status === "completed" && allowMemberToComplete && (
          <button
            onClick={handleMarkUncomplete}
            disabled={loading}
            className="w-full py-2 flex items-center justify-center gap-2 rounded-xl border border-amber-500 text-amber-500 hover:bg-amber-500/10 disabled:opacity-50 text-sm font-semibold transition-colors"
          >
            {loading ? "Updating..." : (
              <>
                <XCircle className="h-4 w-4" />
                Mark as Uncompleted
              </>
            )}
          </button>
        )}

        {session.workouts?.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Workouts</p>
            <Accordion type="multiple" className="space-y-2">
              {session.workouts.map((w, idx) => (
                <AccordionItem
                  key={w.id || idx}
                  value={w.id || `workout-${idx}`}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  <AccordionTrigger className="px-3 py-2 hover:no-underline hover:bg-secondary/50">
                    <div className="flex items-center gap-3 text-left">
                      <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                        {w.image
                          ? <img src={`${BACKEND_URL}${w.image}`} alt={w.title} className="h-full w-full object-cover" />
                          : <Dumbbell className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <span className="font-medium text-sm">{w.title || "Workout"}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <div className="space-y-3 pt-2 border-t border-border">
                      {w.image && (
                        <img
                          src={`${BACKEND_URL}${w.image}`}
                          alt={w.title}
                          className="w-full h-32 object-cover rounded-md"
                        />
                      )}
                      {w.description && (
                        <div>
                          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Description</p>
                          <p className="text-xs text-muted-foreground">
                            <span dangerouslySetInnerHTML={{ __html: w.description }} />
                          </p>
                        </div>
                      )}
                      {(w.groupNames?.length > 0 || w.tagNames?.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {w.groupNames?.map((name, i) => (
                            <Badge key={`g-${i}`} variant="outline" className="text-[10px]">{name}</Badge>
                          ))}
                          {w.tagNames?.map((name, i) => (
                            <Badge key={`t-${i}`} variant="secondary" className="text-[10px]">{name}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No workouts listed for this day.</p>
        )}
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold"
        >Close</button>
      </div>
    </div>
  );
}

// ─── WorkoutCalendar ───────────────────────────────────────────────────────────
function WorkoutCalendar({ schedule, onSelectSession }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const sessionMap = useMemo(() => {
    const map = {};
    schedule.forEach(s => { if (s.date) map[s.date] = s; });
    return map;
  }, [schedule]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const grid = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [
      ...Array(firstDay).fill(null),
      ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [year, month]);

  const todayYMD = toYMD(today);

  return (
    <>
      <Card className="bg-card border-border">
        <CardContent className="p-3">

          {/* Month nav */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevMonth}
              className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-border transition-colors"
            ><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-semibold text-sm">{MONTH_NAMES[month]} {year}</span>
            <button
              onClick={nextMonth}
              className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-border transition-colors"
            ><ChevronRight className="h-4 w-4" /></button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {CAL_HEADERS.map(h => (
              <div key={h} className="text-center text-[10px] font-mono uppercase text-muted-foreground py-1">{h}</div>
            ))}
          </div>

          {/* Calendar rows — NO mx on cells, use gap-y-0.5 */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {grid.flat().map((day, idx) => {
              if (!day) return <div key={idx} />;
              const ymd = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const session = sessionMap[ymd];
              const isToday = ymd === todayYMD;
              const st = session ? (STATUS_CLS[session.status] || STATUS_CLS.upcoming) : null;
              return (
                <button
                  key={idx}
                  onClick={() => session && onSelectSession(session)}
                  disabled={!session}
                  className={[
                    "flex flex-col items-center justify-center py-1 rounded-lg w-full",
                    "transition-colors select-none",
                    session ? "cursor-pointer hover:bg-secondary active:bg-border" : "cursor-default",
                    isToday ? "ring-1 ring-primary" : "",
                  ].join(" ")}
                >
                  <span className={`text-xs leading-none font-medium ${isToday ? "text-primary font-bold" : "text-foreground"
                    }`}>{day}</span>
                  {session ? (
                    <>
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full ${st.bg}`} />
                      <span className={`text-[8px] leading-tight font-mono ${st.text}`}>
                        {session.workouts?.length || 0}W
                      </span>
                    </>
                  ) : (
                    <span className="mt-1 w-1.5 h-1.5" /> /* spacer so all rows equal height */
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
            {Object.entries(STATUS_CLS).map(([key, s]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${s.bg}`} />
                <span className="text-[10px] text-muted-foreground">{s.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </>
  );
}

// ─── AllSchedules ──────────────────────────────────────────────────────────────
function AllSchedules({ schedule, onSelectSession }) {
  const [filter, setFilter] = useState("all");
  const filters = ["all", "upcoming", "completed", "missed"];
  const filtered = filter === "all" ? schedule : schedule.filter(s => s.status === filter);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-0 pt-4 px-4">
        <CardTitle className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-3">All Schedules</CardTitle>
        {/* Filter pills — full-width row, no wrap overflow */}
        <div className="flex w-full rounded-lg overflow-hidden border border-border text-xs">
          {filters.map((f, i) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={[
                "flex-1 py-1.5 capitalize font-medium transition-colors text-center",
                i < filters.length - 1 ? "border-r border-border" : "",
                filter === f
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >{f}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-3 px-4 pb-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
            <Clock className="h-7 w-7 opacity-40" />
            <p className="text-sm">No sessions found</p>
          </div>
        ) : (
          <div>
            {filtered.map((s, i) => {
              const st = STATUS_CLS[s.status] || STATUS_CLS.upcoming;
              const Icon = st.icon;
              const dateLabel = s.date
                ? parseLocalDate(s.date)?.toLocaleDateString("en-IN", {
                  weekday: "short", day: "numeric", month: "short"
                })
                : s.date;
              return (
                <div
                  key={i}
                  onClick={() => onSelectSession(s)}
                  className="py-3 border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40 px-2 -mx-2 rounded-xl transition-colors"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 shrink-0 ${st.text}`} />
                        <span className="text-sm font-medium truncate">{dateLabel}</span>
                        <span className="text-[10px] uppercase text-muted-foreground font-mono shrink-0">
                          {s.dayOfWeek?.slice(0, 3)}
                        </span>
                      </div>
                      {s.status === "completed" && s.completedAt && (() => {
                        const date = new Date(s.completedAt);
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const day = date.getDate();
                        const month = months[date.getMonth()];
                        let hours = date.getHours();
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        const ampm = hours >= 12 ? "pm" : "am";
                        hours = hours % 12;
                        hours = hours ? hours : 12;
                        const timeStr = `${day} ${month}, ${hours}:${minutes} ${ampm}`;
                        const isMember = s.completedBy?.startsWith("member:");
                        return (
                          <span className="text-[10px] text-muted-foreground pl-6 truncate">
                            Done by {isMember ? "Member" : "Staff"}: {s.completedByName || (isMember ? "Member" : "Staff")} • {timeStr}
                          </span>
                        );
                      })()}
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${st.light} ${st.text}`}>
                      {st.label}
                    </span>
                  </div>
                  {s.workouts?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pl-6">
                      {s.workouts.map((w, wi) => (
                        <span key={wi} className="text-xs bg-secondary px-2 py-0.5 rounded-md text-muted-foreground">
                          {w.title || "Workout"}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── WeeklyPattern ─────────────────────────────────────────────────────────────
function WeeklyPattern({ weekPlan }) {
  if (!Object.keys(weekPlan).length) return null;
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3">Weekly Pattern</p>

        {/* 7-col grid — equal columns, no gap overflow */}
        <div className="grid grid-cols-7">
          {DAY_ORDER.map(day => {
            const wks = weekPlan[day] || [];
            const active = wks.length > 0;
            return (
              <div
                key={day}
                className={`flex flex-col items-center gap-1 py-2 mx-px rounded-lg ${active ? "bg-primary/10" : "bg-secondary"
                  }`}
              >
                <span className={`text-[10px] font-mono font-bold leading-none ${active ? "text-primary" : "text-muted-foreground"
                  }`}>{DAY_LABEL[day][0]}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-primary" : "bg-border"
                  }`} />
                {active && (
                  <span className="text-[8px] text-primary font-mono font-medium leading-none">{wks.length}W</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Workout name list below the grid */}
        <div className="mt-3 space-y-1.5">
          {DAY_ORDER.filter(d => (weekPlan[d] || []).length > 0).map(day => (
            <div key={day} className="flex items-start gap-2">
              <span className="text-[10px] font-mono uppercase text-muted-foreground w-6 pt-0.5 shrink-0">
                {DAY_LABEL[day][0]}
              </span>
              <div className="flex flex-wrap gap-1 min-w-0">
                {(weekPlan[day] || []).map((w, i) => (
                  <span key={i} className="text-[10px] bg-secondary text-foreground px-1.5 py-0.5 rounded font-medium">
                    {w.title}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function MemberWorkouts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("calendar");
  const [selectedSession, setSelectedSession] = useState(null);

  const fetchWorkouts = async () => {
    const token = localStorage.getItem("member_token");
    if (!token) { navigate("/"); return; }
    try {
      const res = await api.get("/member-portal/workouts", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) { navigate("/"); return; }
      setError("Failed to load workouts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkouts();
  }, [navigate]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
      <AlertCircle className="h-8 w-8" /><p>{error}</p>
    </div>
  );

  const stats = data?.stats;
  const schedule = data?.schedule || [];
  const weekPlan = data?.weeklyPlan || {};
  const trainer = data?.trainer;
  const noAssignment = !data?.assignment;

  const TABS = [
    { key: "calendar", label: "Calendar", icon: CalendarDays },
    { key: "schedules", label: "All Schedules", icon: TrendingUp },
  ];

  return (
    <div className="flex flex-col gap-4 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black font-heading uppercase tracking-wide">Workouts</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Your assigned workout plan</p>
      </div>

      {noAssignment ? (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center py-14 gap-3 text-muted-foreground">
            <Dumbbell className="h-10 w-10 opacity-30" />
            <p className="text-sm">No workout plan assigned yet</p>
            <p className="text-xs text-center max-w-xs">Contact the gym staff to get a personalised workout plan.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats — 2-col on mobile, 4-col on sm+ */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard icon={CheckCircle2} label="Completed" value={stats?.completed ?? 0} color="text-green-500" />
            <StatCard icon={XCircle} label="Missed" value={stats?.missed ?? 0} color="text-red-500" />
            <StatCard icon={Clock} label="Upcoming" value={stats?.remaining ?? 0} color="text-amber-400" />
            <StatCard icon={TrendingUp} label="Total" value={stats?.total ?? 0} />
          </div>

          {/* Trainer */}
          {trainer && (
            <Card className="bg-card border-border">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Trainer</p>
                  <p className="font-semibold truncate">{trainer.name}</p>
                  {trainer.email && <p className="text-xs text-muted-foreground truncate">{trainer.email}</p>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Weekly pattern */}
          <WeeklyPattern weekPlan={weekPlan} />

          {/* Tab switcher — equal halves, border-r divider between */}
          <div className="flex w-full rounded-xl overflow-hidden border border-border">
            {TABS.map(({ key, label, icon: Icon }, i) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={[
                  "flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
                  i < TABS.length - 1 ? "border-r border-border" : "",
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="h-4 w-4 shrink-0" />{label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "calendar" && <WorkoutCalendar schedule={schedule} onSelectSession={setSelectedSession} />}
          {tab === "schedules" && <AllSchedules schedule={schedule} onSelectSession={setSelectedSession} />}
        </>
      )}
      {selectedSession && (
        <DayDetail
          session={selectedSession}
          allowMemberToComplete={data?.assignment?.allowMemberToComplete}
          onClose={() => setSelectedSession(null)}
          onSuccess={fetchWorkouts}
        />
      )}
    </div>
  );
}
